import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";

// Shows only when the worklist count drifts from today's report row count and explains why, row by row.
export function WorklistReconcileBanner({ reportRows, onList, fetchReconcile, applyReconcile, onApplied, snapshotKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const drift = reportRows != null && onList != null && reportRows !== onList;
  useEffect(() => {
    if (!drift) { setData(null); return; }
    fetchReconcile().then(setData).catch(() => setData(null));
  }, [drift, reportRows, onList, snapshotKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!drift || !data) return null;
  const fixable = (data.extras?.length || 0) + (data.duplicates?.length || 0);
  const apply = async () => {
    setBusy(true);
    try {
      const r = await applyReconcile();
      toast.success(`${r.extras_removed + r.duplicates_hidden} case(s) taken off today's list — worklist ${r.on_list} / report ${r.report_rows}`);
      onApplied?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reconcile"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs space-y-2" data-testid="ageing-reconcile-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900" data-testid="ageing-reconcile-summary">
              Worklist shows {onList} case{onList === 1 ? "" : "s"} but today's report has {reportRows}
            </div>
            <div className="text-amber-800">Every row below is on the list without a matching line in {data.snapshot?.filename || "the uploaded report"}. Notes and P2Ps are kept when a case is taken off the list.</div>
          </div>
        </div>
        {fixable > 0 && (
          <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={apply} disabled={busy} data-testid="ageing-reconcile-apply">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ListChecks className="w-3.5 h-3.5 mr-1" />} Match worklist to report ({fixable})
          </Button>
        )}
      </div>
      {[...(data.extras || []).map((e) => ({ ...e, kind: "Not on report" })), ...(data.duplicates || []).map((d) => ({ ...d, kind: "Duplicate" }))].map((row) => (
        <div key={row.case_id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 bg-white/70 rounded px-2 py-1" data-testid={`ageing-reconcile-row-${row.case_id}`}>
          <span className="font-semibold text-slate-800">{row.customer_name || row.case_id}</span>
          <span className="text-slate-500">{row.sf_number || row.loan_application_id || row.loandisk_loan_id}</span>
          <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide">{row.kind}</span>
          <span className="text-slate-600">{row.reason}</span>
          {row.has_notes && <span className="text-[10px] text-slate-400">has notes</span>}
        </div>
      ))}
      {(data.missing || []).length > 0 && (
        <div className="text-amber-800" data-testid="ageing-reconcile-missing">
          On the report but not on the list: {data.missing.map((m) => `${m.customer_name || m.report_key} (${m.reason})`).join("; ")}
        </div>
      )}
    </div>
  );
}
